// /part/:id — one GPS, LED or pyro unit, the thing a Firefly sticker names.

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { SecondaryAction } from '../components/PageHeader';
import ItemName from '../components/ItemName';
import PageLayout from '../components/ui/PageLayout';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import LogoSpinner from '../components/ui/LogoSpinner';
import Panel from '../components/ui/Panel';
import { Tag } from '../components/ui/Tag';
import useUserStore from '../stores/useUserStore';
import { userCanAccess } from '../navigation/menuConfig';
import { formatTimestamp } from '../utils/date-utils';
import type {
  PartInstance,
  PartInstanceBoardType,
  PartInstanceDetail,
  PartInstanceStatus,
} from './types';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded';
const WHEN = 'MMM D, YYYY h:mm A';

const BOARD_LABEL: Record<PartInstanceBoardType, string> = {
  GPS: 'GPS board',
  LED: 'LED board',
  PYR: 'Pyro board',
};

const STATUS_TONE: Record<PartInstanceStatus, string> = {
  in_stock: 'text-on-surface',
  installed: 'text-green-300',
  retired: 'text-red-300',
  lost: 'text-amber-300',
};

const MANIFEST_STATUS: Record<string, string> = {
  draft: 'Not sent yet',
  sent: 'Sent',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export function statusReading(
  instance: Pick<PartInstance, 'status' | 'installed_on' | 'retired_at'>,
  droneSerial: string | null,
): string {
  switch (instance.status) {
    case 'installed':
      return `Fitted to ${droneSerial || instance.installed_on || 'a drone'}`;
    case 'retired':
      return instance.retired_at ? `Retired ${formatTimestamp(instance.retired_at)}` : 'Retired';
    case 'lost':
      return 'Lost';
    default:
      return 'In stock';
  }
}

const Section: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className = '' }) => (
  <Panel title={title} className={`min-w-0 ${className}`}>
    {children}
  </Panel>
);

const Field: React.FC<{ label: string; children: React.ReactNode; mono?: boolean }> = ({
  label,
  children,
  mono = false,
}) => (
  <div className="min-w-0">
    <dt className="text-xs text-on-surface-variant">{label}</dt>
    <dd className={`mt-0.5 text-sm text-on-surface break-words ${mono ? 'font-mono' : ''}`}>
      {children}
    </dd>
  </div>
);

const PageLoading: React.FC = () => (
  <PageLayout width="narrow" kind="record" className="pt-4">
    <LogoSpinner label="Loading this part" />
  </PageLayout>
);

const PartInstancePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useApi();
  const user = useUserStore((s) => s.user);
  const canOpenBox = userCanAccess({ require: 'receivingHub' }, user);
  // Mirrors the /procurement/board-returns/:id route gate in App.js; a link
  // past it bounces the reader to the dashboard.
  const canOpenManifests = !!(
    user.isadmin || user.can_admin || user.can_procurement || user.can_manufacturing
  );

  const [detail, setDetail] = useState<PartInstanceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<'not_found' | 'error' | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailure(null);
    setDetail(null);

    api
      .get(`/scanner/part-instance/${encodeURIComponent(id)}/detail`)
      .then((data: PartInstanceDetail) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setFailure(err?.status === 404 || err?.status === 400 ? 'not_found' : 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, attempt]);

  if (loading) return <PageLoading />;

  if (failure || !detail) {
    const notFound = failure === 'not_found';
    return (
      <PageLayout width="narrow" kind="record" className="pt-4">
        <EmptyState
          className="mx-auto max-w-xl"
          title={notFound ? 'No part with that sticker.' : 'This part did not load.'}
          action={
            <>
              {!notFound && (
                <SecondaryAction onClick={() => setAttempt((n) => n + 1)}>Try again</SecondaryAction>
              )}
              <SecondaryAction to="/parts/lookup" variant={notFound ? 'tonal' : 'text'}>
                Scan Part
              </SecondaryAction>
            </>
          }
        >
          <span className="block break-all font-mono text-xs">{id}</span>
          <span className="mt-1 block">
            {notFound
              ? 'Check the sticker is a Firefly part sticker, then scan it again.'
              : 'Check your connection, then try again.'}
          </span>
        </EmptyState>
      </PageLayout>
    );
  }

  const { instance, drone, box, installs, manifests } = detail;
  const reading = statusReading(instance, drone?.serialnumber ?? null);
  const vendor = box ? box.vendor_name || box.vendor : null;
  const poNumber = box ? box.po_number || box.purchase_order_ref : null;

  return (
    <PageLayout width="narrow"
      kind="record"
      className="space-y-5 pt-4"
      header={
      <header className="relative pt-3">
        <div
          aria-hidden="true"
          className="absolute left-0 top-0 h-[3px] w-40 sm:w-56 rounded-full bg-gradient-to-r from-primary/80 via-primary/40 to-transparent"
        />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="break-words text-balance">
              <ItemName size="lg" inline item={instance} />
            </h1>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              {instance.board_type && (
                <Tag>{BOARD_LABEL[instance.board_type] ?? instance.board_type}</Tag>
              )}
              <span className={`font-semibold ${STATUS_TONE[instance.status] ?? 'text-on-surface'}`}>
                {reading}
              </span>
              {instance.serial && (
                <span className="text-on-surface-variant">
                  Serial <span className="font-mono text-on-surface">{instance.serial}</span>
                </span>
              )}
              {instance.sku && (
                <span className="font-mono text-xs text-on-surface-variant">{instance.sku}</span>
              )}
            </p>
          </div>
          <div className="flex-shrink-0">
            <SecondaryAction to="/parts/lookup" variant="text">Scan Part</SecondaryAction>
          </div>
        </div>
      </header>
      }
    >

      {drone ? (
        <Link
          to={`/drone/${drone.serialnumber}`}
          className="block rounded-xl bg-surface-container-high p-4 ring-1 ring-primary/40
            hover:bg-surface-container-highest transition-colors duration-150 ease-emphasized
            motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          <span className="block text-sm text-on-surface-variant">Fitted to</span>
          <span className="mt-0.5 block font-mono text-lg text-on-surface break-all">
            {drone.serialnumber}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-on-surface-variant">
            {drone.title && <span className="text-on-surface">{drone.title}</span>}
            {drone.generation != null && drone.generation !== '' && (
              <Tag size="sm">Gen {drone.generation}</Tag>
            )}
            {drone.condition && (
              <Tag size="sm">{drone.condition}</Tag>
            )}
            {instance.installed_at && (
              <span>Since {formatTimestamp(instance.installed_at)}</span>
            )}
          </span>
        </Link>
      ) : (
        <Panel title="Not fitted to a drone">
          <p className="text-xs text-on-surface-variant">
            {installs.length > 0
              ? 'It has been fitted before. The history is below.'
              : 'It has never been fitted to a drone.'}
          </p>
        </Panel>
      )}

      <div className="grid gap-5 sm:grid-cols-3">
        <Section title="Box it came from" className="sm:col-span-2">
          {box ? (
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Box" mono>
                {box.barcode && canOpenBox && instance.receipt_item_id ? (
                  <Link
                    to={`/receiving/item/${instance.receipt_item_id}`}
                    className={`text-primary hover:underline ${FOCUS}`}
                  >
                    {box.barcode}
                  </Link>
                ) : (
                  box.barcode || '—'
                )}
              </Field>
              {box.box_no != null && <Field label="Box number">Box {box.box_no}</Field>}
              <Field label="PO number" mono>{poNumber || '—'}</Field>
              <Field label="Vendor">{vendor || '—'}</Field>
              <Field label="Received">
                {box.received_at ? formatTimestamp(box.received_at) : '—'}
                {box.received_by && (
                  <span className="text-on-surface-variant"> by {box.received_by}</span>
                )}
              </Field>
            </dl>
          ) : (
            <EmptyState variant="line">No box on record for this part.</EmptyState>
          )}
        </Section>

        <Section title="Flashing">
          <p className="text-sm text-on-surface-variant">Not a flashable board.</p>
        </Section>
      </div>

      <Section title="Install history">
        {installs.length === 0 ? (
          <EmptyState variant="line">Never fitted to a drone.</EmptyState>
        ) : (
          <ListStack
            label="Install history"
            on="panel"
            items={installs}
            getKey={(e) => String(e.id)}
            renderRow={(e) => (
              <ListRow>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                  <span className="text-on-surface">
                    {e.action === 'install' ? 'Fitted to' : 'Removed from'}{' '}
                    {e.drone_serial ? (
                      <Link
                        to={`/drone/${e.drone_serial}`}
                        className={`font-mono text-primary hover:underline ${FOCUS}`}
                      >
                        {e.drone_serial}
                      </Link>
                    ) : (
                      'a drone'
                    )}
                  </span>
                  <span className="text-xs text-on-surface-variant tabular-nums whitespace-nowrap">
                    {formatTimestamp(e.occurred_at, WHEN)}
                  </span>
                  <span className="text-xs text-on-surface-variant">
                    {e.operator_name || 'Unknown'}
                  </span>
                </div>
                {e.note && (
                  <p className="mt-0.5 text-xs text-on-surface-variant break-words">{e.note}</p>
                )}
              </ListRow>
            )}
          />
        )}
      </Section>

      {manifests.length > 0 && (
        <Section title="Vendor RMA">
          <ListStack
            label="Vendor RMA"
            on="panel"
            items={manifests}
            getKey={(m) => `${m.manifest_id}-${m.line_number ?? ''}`}
            renderRow={(m) => (
              <ListRow>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm">
                  {canOpenManifests ? (
                    <Link
                      to={`/procurement/board-returns/${m.manifest_id}`}
                      className={`font-mono text-primary hover:underline ${FOCUS}`}
                    >
                      {m.manifest_number}
                    </Link>
                  ) : (
                    <span className="font-mono text-on-surface">{m.manifest_number}</span>
                  )}
                  <span className="text-on-surface">{MANIFEST_STATUS[m.status] ?? m.status}</span>
                  {m.sent_at && m.status !== 'draft' && (
                    <span className="text-xs text-on-surface-variant tabular-nums whitespace-nowrap">
                      Sent {formatTimestamp(m.sent_at)}
                    </span>
                  )}
                  {m.reason_label && (
                    <span className="min-w-0 basis-full text-xs text-on-surface-variant sm:basis-auto">
                      {m.reason_label}
                    </span>
                  )}
                </div>
              </ListRow>
            )}
          />
        </Section>
      )}
    </PageLayout>
  );
};

export default PartInstancePage;
